import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WindowResizeService } from '@core/services/window-resize/window-resize.service';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { UpdateHideModal } from '@stores/popup-event/popup-event.actions';
import { WINDOW } from '@wizbii/angular-utilities';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, Observable } from 'rxjs';

@UntilDestroy()
@Component({
  templateUrl: './cej-modal.component.html',
  styleUrls: ['./cej-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CejModalComponent implements OnInit, AfterViewInit {
  @ViewChild('modalActions', { read: ElementRef }) modalActionsContainer!: ElementRef;

  modalActionsVisible$ = new BehaviorSubject<boolean>(true);
  modalActionsObserver!: IntersectionObserver;
  isMobile = true;
  hideModalControl = false;

  private readonly trackingCategory = 'CEJ';

  readonly illustrationSrc = `${environment.wizbiiImages}/campagne-popup/cej-modale-illustration.jpg`;
  readonly illustrationMobileSrc = `${environment.wizbiiImages}/campagne-popup/cej-modale-illustration-mobile.jpg`;

  trackEvent = trackEventWithAtInternet;
  FeaturesRoutingEnum = FeaturesRoutingEnum;

  get ratio$(): Observable<number> {
    return this.windowResizeService.ratio$;
  }

  constructor(
    private readonly store: Store,
    readonly dialogRef: MatDialogRef<CejModalComponent>,
    private readonly windowResizeService: WindowResizeService,
    @Inject(MAT_DIALOG_DATA) public data: { isMobile: boolean },
    @Inject(WINDOW) private readonly window: any
  ) {
    this.isMobile = data.isMobile;
  }

  ngOnInit(): void {
    this.trackEvent({
      category: this.trackingCategory,
      action: 'Display Modale',
      type: 'action',
    });

    if (this.isMobile) {
      this.ratio$.pipe(untilDestroyed(this)).subscribe({
        next: (ratio) => {
          this.dialogRef.updateSize('100vw', `${100 * ratio - 20}px`);
          this.dialogRef.addPanelClass('wizbii-mobile-panel-open');
        },
      });
    }

    this.modalActionsObserver = new IntersectionObserver(
      (entries, _) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          this.modalActionsVisible$.next(entry.isIntersecting);
        });
      },
      { rootMargin: '-70px 0px -70px 0px' }
    );

    this.dialogRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe({ next: () => this.closeDialog('', false) });
  }

  ngAfterViewInit(): void {
    if (this.modalActionsContainer) {
      this.modalActionsObserver.observe(this.modalActionsContainer.nativeElement);
    }
  }

  hideModalForever(): void {
    this.store.dispatch(new UpdateHideModal(true));
  }

  closeDialog(label: string, hideModalForever: boolean): void {
    if (label === 'Click Je me lance') {
      this.trackEvent({
        category: this.trackingCategory,
        action: `Click Je me lance`,
        type: 'action',
      });
    } else {
      this.trackEvent({
        category: this.trackingCategory,
        action: 'Click Close',
        type: 'action',
        label,
      });
    }

    if (hideModalForever || this.hideModalControl) {
      this.hideModalForever();
    }

    if (this.isMobile) {
      this.dialogRef.addPanelClass('wizbii-mobile-panel-close');
      this.window.setTimeout(() => {
        this.dialogRef.close();
      }, 100);
    } else {
      this.dialogRef.close();
    }
  }

  switch(): void {
    this.hideModalControl = !this.hideModalControl;

    if (this.hideModalControl) {
      this.trackEvent({
        category: this.trackingCategory,
        action: 'Click Hide Message',
        type: 'action',
      });
    }
  }
}
